import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Button, TextField, CircularProgress, Alert, Avatar, Stack, Divider, IconButton, Card, CardContent, Grid, useMediaQuery, Chip, FormHelperText, OutlinedInput
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getShops, createAdvertisement } from '../../../utils/api';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CampaignIcon from '@mui/icons-material/Campaign';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';

const Input = styled('input')({
  display: 'none',
});

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function Advertisement() {
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState('');
  const [availablePincodes, setAvailablePincodes] = useState([]);
  const [selectedPincodes, setSelectedPincodes] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);
  const [maxInvoices, setMaxInvoices] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [adDetails, setAdDetails] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    loadShops();
  }, []);

  useEffect(() => {
    if (selectedShop) {
      // Get pincode only from the selected company
      const selectedCompany = shops.find(shop => shop.id === selectedShop);
      if (selectedCompany && selectedCompany.pincode) {
        setAvailablePincodes([selectedCompany.pincode]);
      } else {
        setAvailablePincodes([]);
      }
    } else {
      setAvailablePincodes([]);
      setSelectedPincodes([]);
    }
  }, [selectedShop, shops]);

  const loadShops = async () => {
    try {
      setLoadingMore(true);
      const res = await getShops(page, 20);
      const newShops = res.data?.results || [];
      setShops(prev => page === 1 ? newShops : [...prev, ...newShops]);
      setHasMore(newShops.length === 20);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
    loadShops();
  };

  const handleShopChange = (e) => {
    setSelectedShop(e.target.value);
    setSelectedPincodes([]); // Reset selected pincodes when shop changes
  };

  const handlePincodeChange = (event) => {
    const value = event.target.value;
    setSelectedPincodes(typeof value === 'string' ? value.split(',') : value);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
      setImagePreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setAdDetails(null);
    
    if (!selectedShop || !image || !startDateTime || !endDateTime) {
      setError('Please fill all required fields and upload an image.');
      return;
    }
    if (endDateTime <= startDateTime) {
      setError('End date & time must be after start date & time.');
      return;
    }

    if (maxInvoices < 0) {
      setError('Maximum invoices cannot be negative.');
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('image', image);
      formData.append('start_time', new Date(startDateTime).toISOString().slice(0, 19));
      formData.append('end_time', new Date(endDateTime).toISOString().slice(0, 19));
      
      // Add selected pincodes as comma-separated string
      if (selectedPincodes.length > 0) {
        formData.append('target_pincodes', selectedPincodes.join(','));
      }
      formData.append('max_invoices', maxInvoices.toString());
      
      const response = await createAdvertisement(selectedShop, formData);
      const data = response.data;
      setSuccess('Advertisement submitted successfully!');
      setAdDetails(data.advertisement);
      
      // Reset form
      setSelectedShop('');
      setSelectedPincodes([]);
      setImage(null);
      setImagePreview(null);
      setStartDateTime(null);
      setEndDateTime(null);
      setMaxInvoices(0);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to submit advertisement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'top', justifyContent: 'center', py: { xs: 2, md: 6 }, px: { xs: 1, md: 0 } }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, width: '100%', borderRadius: 4, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mx: 'auto' }}>
        <Box display="flex" alignItems="center" mb={3} gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
          <CampaignIcon sx={{ color: 'text.secondary', fontSize: { xs: 36, md: 48 } }} />
          <Box>
            <Typography variant="h3" fontWeight={800} color="text.primary" lineHeight={1.1} sx={{ fontSize: { xs: 22, md: 32 } }}>
              Advertisement
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: 15, md: 18 } }}>
              Promote your business to customers
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 4 }} />
        
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Grid container spacing={3} alignItems="stretch">
            {/* Left Column: Business & Image */}
            <Grid item xs={12} lg={6}>
              <Stack spacing={3}>
                {/* Business Selection */}
                <SectionCard>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" fontWeight={700} mb={2} sx={{ fontSize: { xs: 18, md: 22 } }}>
                      Select Business
                    </Typography>
                    <FormControl fullWidth size="large">
                      <InputLabel id="shop-label">Business *</InputLabel>
                      <Select
                        labelId="shop-label"
                        value={selectedShop}
                        label="Business *"
                        onChange={handleShopChange}
                        sx={{ fontSize: { xs: 16, md: 18 }, minHeight: { xs: 44, md: 56 } }}
                      >
                        {shops.map(shop => (
                          <MenuItem key={shop.id} value={shop.id} sx={{ fontSize: { xs: 16, md: 18 }, minHeight: { xs: 40, md: 48 } }}>
                            <Box>
                              <Typography variant="body1" fontWeight={600}>
                                {shop.name}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                        {hasMore && !loadingMore && (
                          <MenuItem onClick={handleLoadMore} sx={{ justifyContent: 'center', color: 'primary.main' }}>
                            Load More...
                          </MenuItem>
                        )}
                        {loadingMore && (
                          <MenuItem sx={{ justifyContent: 'center' }}>
                            <CircularProgress size={20} />
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </CardContent>
                </SectionCard>

                <SectionCard>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" fontWeight={700} mb={2}>
                      Advertisement Image *
                    </Typography>
                    <FormControl fullWidth>
                      <label htmlFor="ad-image-upload">
                        <Input
                          accept="image/*"
                          id="ad-image-upload"
                          type="file"
                          onChange={handleImageChange}
                        />
                        <Button 
                          variant="outlined" 
                          component="span" 
                          fullWidth 
                          startIcon={<CloudUploadIcon />} 
                          sx={{ 
                            fontSize: { xs: 16, md: 18 }, 
                            minHeight: { xs: 48, md: 56 }, 
                            borderStyle: 'dashed', 
                            borderWidth: 2, 
                            borderColor: 'divider', 
                            color: 'text.primary', 
                            mb: 2 
                          }}
                        >
                          {image ? image.name : 'Upload Advertisement Image'}
                        </Button>
                      </label>
                      {imagePreview && (
                        <Box mt={2} display="flex" flexDirection="column" alignItems="center" gap={1}>
                          <Avatar 
                            variant="rounded" 
                            src={imagePreview} 
                            alt="Ad Preview" 
                            sx={{ 
                              width: { xs: 200, md: 240 }, 
                              height: { xs: 200, md: 240 }, 
                              border: '1px solid', 
                              borderColor: 'divider', 
                              mb: 1 
                            }} 
                          />
                          <IconButton color="error" onClick={handleRemoveImage} size="large">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </FormControl>
                  </CardContent>
                </SectionCard>
              </Stack>
            </Grid>

            {/* Right Column: Timing & Targeting */}
            <Grid item xs={12} lg={6}>
              <Stack spacing={3}>
                {/* Advertisement Timing */}
                <SectionCard>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" fontWeight={700} mb={2} sx={{ fontSize: { xs: 18, md: 22 } }}>
                      Advertisement Timing *
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Stack spacing={3}>
                        <DateTimePicker
                          label="Start Date & Time *"
                          value={startDateTime}
                          onChange={setStartDateTime}
                          renderInput={(params) => (
                            <TextField 
                              {...params} 
                              fullWidth
                              sx={{ fontSize: { xs: 15, md: 18 } }} 
                              size={isMobile ? 'small' : 'medium'} 
                            />
                          )}
                          minDateTime={new Date()}
                        />
                        <DateTimePicker
                          label="End Date & Time *"
                          value={endDateTime}
                          onChange={setEndDateTime}
                          renderInput={(params) => (
                            <TextField 
                              {...params} 
                              fullWidth
                              sx={{ fontSize: { xs: 15, md: 18 } }} 
                              size={isMobile ? 'small' : 'medium'} 
                            />
                          )}
                          minDateTime={startDateTime || new Date()}
                        />
                      </Stack>
                    </LocalizationProvider>
                  </CardContent>
                </SectionCard>

                <SectionCard>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" fontWeight={700} mb={2} sx={{ fontSize: { xs: 18, md: 22 } }}>
                      <LocationOnIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Target Pincodes
                    </Typography>
                    <Stack spacing={3}>
                      <FormControl fullWidth>
                        <InputLabel id="pincode-label">Target Pincodes</InputLabel>
                        <Select
                          labelId="pincode-label"
                          multiple
                          value={selectedPincodes}
                          onChange={handlePincodeChange}
                          input={<OutlinedInput label="Target Pincodes" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                          MenuProps={MenuProps}
                          disabled={!selectedShop}
                          size={isMobile ? 'small' : 'medium'}
                          sx={{ fontSize: { xs: 15, md: 18 } }}
                        >
                          {availablePincodes.map((pincode) => {
                            return (
                              <MenuItem key={pincode} value={pincode}>
                                <Box>
                                  <Typography variant="body1" fontWeight={600}>
                                    {pincode}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          })}
                          {availablePincodes.length === 0 && selectedShop && (
                            <MenuItem disabled>
                              <Typography variant="body2" color="text.secondary">
                                No pincode available for selected business
                              </Typography>
                            </MenuItem>
                          )}
                          {!selectedShop && (
                            <MenuItem disabled>
                              <Typography variant="body2" color="text.secondary">
                                Please select a business first
                              </Typography>
                            </MenuItem>
                          )}
                        </Select>
                        <FormHelperText>
                          Select target pincodes based on business locations (optional)
                        </FormHelperText>
                      </FormControl>
                      {selectedPincodes.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" mb={1}>
                            Selected Pincodes ({selectedPincodes.length}):
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {selectedPincodes.map((pincode) => {
                              return (
                                <Chip
                                  key={pincode}
                                  label={`${pincode}`}
                                  variant="outlined"
                                  size="small"
                                  color="primary"
                                  onDelete={() => setSelectedPincodes(prev => prev.filter(p => p !== pincode))}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </SectionCard>

                {/* Invoice Limit */}
                <SectionCard>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" fontWeight={700} mb={2} sx={{ fontSize: { xs: 18, md: 22 } }}>
                      <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Display Limit
                    </Typography>
                    <FormControl fullWidth>
                      <TextField
                        label="Maximum Invoices"
                        type="number"
                        value={maxInvoices}
                        onChange={(e) => setMaxInvoices(parseInt(e.target.value) || 0)}
                        placeholder="0"
                        helperText="Maximum number of invoices to show this ad (0 for unlimited)"
                        size={isMobile ? 'small' : 'medium'}
                        sx={{ fontSize: { xs: 15, md: 18 } }}
                        inputProps={{ min: 0 }}
                      />
                    </FormControl>
                  </CardContent>
                </SectionCard>
              </Stack>
            </Grid>
          </Grid>

          {/* Error/Success Messages & Submit Button */}
          <Box mt={4}>
            {error && (
              <Alert severity="error" sx={{ fontSize: { xs: 16, md: 18 }, mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ fontSize: { xs: 16, md: 18 }, mb: 2 }}>
                {success}
              </Alert>
            )}
            <Box display="flex" justifyContent="center">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ 
                  fontWeight: 700, 
                  minHeight: { xs: 48, md: 56 }, 
                  fontSize: { xs: 16, md: 18 }, 
                  px: { xs: 4, md: 6 },
                  minWidth: { xs: 200, md: 250 }
                }}
              >
                {loading ? <CircularProgress size={28} color="inherit" /> : 'Submit Advertisement'}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}